import { makeAutoObservable } from "mobx";
import { BreadcrumbItem } from "src/appStore";
import { ZAttrMeta } from "src/business/attribute";
import {
  createSpecification,
  getSpecTitle,
  loadSchemaLinks,
  loadSecondarySchemaLinks,
  loadSpecification,
  saveSpecification,
  SpecificationType,
  ZSchemaLinks,
  ZSecondarySchemaLinks,
  ZSpecification,
  ZSpecificationCard,
  zSpecificationCard,
} from "src/business/specification";
import { specification2card } from "src/business/specification/specification2card";
import { loadFromStorage, saveToStorage } from "src/common/localStorageApi";
import { MdmBrCr, mdmBreadcrumbs } from "src/common/mdmBreadcrumbs";
import { mdmDeepEqual } from "src/common/mdmDeepEqual";
import { onError } from "src/common/onError";
import { RemoteData } from "src/common/RemoteData";
import { MFormStore } from "src/components/MForm/MFormStore";
import { ZMfNode } from "src/components/MForm/ZMfNode";

import { AttrFeatures } from "../AttrsListEditor/AttrFeatures";
import { clearTmpUids } from "./SpecEditorFormV2/clearTmpUids";

export type SpecTabKey = "general" | "links" | "systemData";

type TabItem = {
  key: SpecTabKey;
  label: string;
};

type ParamsSpecCommonCard = {
  specType: SpecificationType;
  newTitle: string;
  attrFeatures?: AttrFeatures;
  middleCrumb: MdmBrCr;
};

export type SpecLinksCommon =
  | { specType: "primary"; data: ZSchemaLinks }
  | { specType: "secondary"; data: ZSecondarySchemaLinks };

type SpecEditorVersion = "v1" | "v2";
const defaultEditorVersion: SpecEditorVersion = "v1";
const keyEditorVersion = "SpecEditorVersion";

export class ManagementSpecCommonCardStore {
  constructor(private params: ParamsSpecCommonCard) {
    makeAutoObservable(this);
  }

  editorVersion: SpecEditorVersion = defaultEditorVersion;

  setEditorVersion(ver: SpecEditorVersion) {
    this.editorVersion = ver;
  }

  changeEditorVersion(ver: SpecEditorVersion) {
    this.setEditorVersion(ver);
    this.saveEditorVersion();
  }

  loadEditorVersion() {
    this.setEditorVersion(
      loadFromStorage(keyEditorVersion, defaultEditorVersion),
    );
  }

  saveEditorVersion() {
    saveToStorage(keyEditorVersion, this.editorVersion);
  }

  get attrFeatures() {
    return this.params.attrFeatures;
  }

  get specType(): SpecificationType {
    return this.params.specType;
  }

  id = "";

  setId(id: string) {
    this.id = id;
  }

  async init(id: string) {
    this.setId(id);
    this.loadEditorVersion();

    try {
      this.setData({ status: "wait" });
      const result: ZSpecification = this.isNew
        ? {
            id: undefined as unknown as string,
            displayId: undefined as unknown as number,
            name: "",
            displayName: "",
            description: "",
            definition: {
              attributes: [],
            },
          }
        : await loadSpecification(this.specType, id);
      this.setData({ status: "ready", result });
      if (!this.isNew) {
        this.initLinks(id).catch(onError);
      } else {
        // Reset links data and active tab when creating a new specification
        this.setLinksData({ status: "none" });
        this.setActiveTab("general");
      }
    } catch (error) {
      this.setData({ status: "error", error });
    }
  }

  data: RemoteData<ZSpecification> = { status: "none" };

  setData(newData: RemoteData<ZSpecification>) {
    this.setIsChanged(false);
    this.data = newData;
  }

  linksData: RemoteData<SpecLinksCommon> = { status: "none" };

  setLinksData(newData: RemoteData<SpecLinksCommon>) {
    this.linksData = newData;
  }

  get tabsItems(): TabItem[] {
    const { isNew, linksData } = this;
    const items: TabItem[] = [{ key: "general", label: "Общее" }];
    if (!isNew && linksData.status !== "none") {
      items.push({ key: "links", label: "Связи" });
    }
    if (!isNew) {
      items.push({ key: "systemData", label: "Системные данные" });
    }
    return items;
  }

  activeTab: SpecTabKey = "general";

  setActiveTab(tab: SpecTabKey) {
    this.activeTab = tab;
  }

  async initLinks(id: string) {
    try {
      let result: SpecLinksCommon | undefined;
      switch (this.specType) {
        case "primary":
          this.setLinksData({ status: "wait" });
          result = {
            specType: "primary",
            data: await loadSchemaLinks(id),
          };
          break;
        case "secondary":
          this.setLinksData({ status: "wait" });
          result = {
            specType: "secondary",
            data: await loadSecondarySchemaLinks(id),
          };
          break;
        default:
          break;
      }

      this.setLinksData(
        result ? { status: "ready", result } : { status: "none" },
      );
    } catch (error) {
      this.setLinksData({ status: "error", error });
    }
  }

  get isNew(): boolean {
    return this.id === "new";
  }

  get pageTitle(): string {
    if (this.isNew) return this.params.newTitle;
    const { data } = this;
    return (data.status === "ready" && getSpecTitle(data.result)) || "...";
  }

  saving = false;

  setSaving(value: boolean) {
    this.saving = value;
  }

  async updateTask(
    rawValues: unknown,
    task: (values: ZSpecificationCard) => Promise<ZSpecification>,
  ): Promise<ZSpecification> {
    try {
      this.setSaving(true);
      const values = zSpecificationCard.parse(rawValues);
      clearTmpUids(values.definition?.attributes);
      const result = await task(values);
      this.setData({ status: "ready", result });
      return result;
    } finally {
      this.setSaving(false);
    }
  }

  // TODO: Пока нет разделения функций для разных типов спецификаций

  async save(rawValues: unknown): Promise<ZSpecification> {
    return this.updateTask(rawValues, (values) =>
      saveSpecification(this.specType, this.id, values),
    );
  }

  async create(rawValues: unknown): Promise<ZSpecification> {
    return this.updateTask(rawValues, (values) =>
      createSpecification(this.specType, values),
    );
  }

  get initialData(): ZSpecificationCard | undefined {
    const { data } = this;
    return data.status === "ready"
      ? specification2card(data.result)
      : undefined;
  }

  isChanged = false;

  setIsChanged(value: boolean) {
    this.isChanged = value;
  }

  onChange(values: ZSpecificationCard) {
    // Необходимо избавиться от Proxy. Иначе сравнение работает неправильно
    const src = JSON.parse(JSON.stringify(this.initialData));
    const current = JSON.parse(JSON.stringify(values));
    this.setIsChanged(!mdmDeepEqual(src, current));
  }

  get crumbs(): BreadcrumbItem[] {
    return mdmBreadcrumbs([
      { type: "ObjAndSpecs" },
      this.params.middleCrumb,
      { type: "Name", name: this.pageTitle },
    ]);
  }

  testMForm: MFormStore | null = null;

  setTestMForm(store: MFormStore | null) {
    this.testMForm = store;
  }

  onTest(values: unknown) {
    try {
      const card = zSpecificationCard.parse(values);

      const attributes: ZAttrMeta[] = JSON.parse(
        JSON.stringify(card.definition?.attributes ?? []),
      );
      sortAttributes(attributes);

      const node: ZMfNode = {
        type: "grid",
        nodes: attributes.map((attr) => ({ type: "attr", attr })),
      };
      const formStore = new MFormStore();
      formStore.setMeta(node);
      this.setTestMForm(formStore);
    } catch (e) {
      onError(e);
    }
  }
}

const sortAttributes = (attrList: ZAttrMeta[]) => {
  attrList.sort((a, b) => {
    const orderA = a.edit?.order ?? Number.MAX_SAFE_INTEGER;
    const orderB = b.edit?.order ?? Number.MAX_SAFE_INTEGER;
    return orderA - orderB;
  });
  attrList.forEach(({ type }) => {
    if (type.name === "list") {
      sortAttributes(type.fields ?? []);
    }
  });
};
